import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ShieldCheck, Lock, CreditCard, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function PaymentPage() {
  const { t, language, formatPrice, BACKEND_URL } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const reservationId = searchParams.get('reservation_id');
  const bookingRef = searchParams.get('booking_ref');
  const token = searchParams.get('token');
  const isTestCallback = searchParams.get('test') === 'true';
  const paymentError = searchParams.get('error');

  const [processing, setProcessing] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);

  useEffect(() => {
    if (paymentError === 'payment_failed') {
      toast.error(language === 'tr'
        ? 'Ödeme tamamlanamadı. Lütfen kart bilgilerinizi kontrol edip tekrar deneyin.'
        : 'Payment could not be completed. Please check your card details and try again.');
    }
  }, [paymentError]);

  useEffect(() => {
    if (token && isTestCallback) {
      handleTestCallback(token);
    }
  }, [token, isTestCallback]);

  const handleTestCallback = async (tok) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/payment/callback?token=${tok}&test=true`);
      if (res.data.status === 'success') {
        navigate(`/confirmation/${res.data.booking_ref}`);
      } else {
        toast.error(language === 'tr' ? 'Ödeme doğrulanamadı' : 'Payment could not be verified');
        navigate('/rooms');
      }
    } catch {
      toast.error(language === 'tr' ? 'Hata oluştu' : 'An error occurred');
      navigate('/rooms');
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const ipRes = await fetch('https://api.ipify.org?format=json').then(r => r.json()).catch(() => ({ ip: '85.34.78.112' }));
      const res = await axios.post(`${BACKEND_URL}/api/payment/initialize`, {
        reservation_id: reservationId,
        ip_address: ipRes.ip || '85.34.78.112',
      });
      if (res.data.status === 'test_mode' || res.data.status === 'success') {
        if (res.data.status === 'test_mode') {
          setIsTestMode(true);
          toast.info(language === 'tr' ? 'Test modunda çalışıyor - ödeme simüle edilecek' : 'Test mode - payment will be simulated');
          setTimeout(() => { window.location.href = res.data.payment_url; }, 2000);
        } else {
          window.location.href = res.data.payment_url;
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || (language === 'tr' ? 'Ödeme başlatılamadı' : 'Could not start payment');
      toast.error(msg);
      setProcessing(false);
    }
  };

  if (token && isTestCallback) {
    return (
      <div style={{ minHeight: '100vh', background: '#F6F1E7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Manrope, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={32} style={{ color: '#1F3A2E', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: '#6E7F5B' }}>{language === 'tr' ? 'Ödeme doğrulanıyor...' : 'Verifying payment...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F6F1E7', fontFamily: 'Manrope, sans-serif' }}>
      <Navbar />
      <div style={{ maxWidth: '480px', margin: '0 auto', padding: '3rem 1.5rem' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ width: '60px', height: '60px', background: 'rgba(31,58,46,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <ShieldCheck size={28} style={{ color: '#1F3A2E' }} />
          </div>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', fontWeight: 600, color: '#1F3A2E', marginBottom: '6px' }}>{t('payment_title')}</h1>
          <p style={{ fontSize: '14px', color: '#6E7F5B' }}>{t('payment_desc')}</p>
        </div>

        {/* Booking Ref */}
        <div style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '16px', padding: '20px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('booking_ref')}</span>
            <code style={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: 800, color: '#1F3A2E', background: '#F6F1E7', padding: '4px 12px', borderRadius: '8px' }}>{bookingRef}</code>
          </div>
          <div style={{ borderTop: '1px solid #EFE7DA', paddingTop: '10px' }}>
            <p style={{ fontSize: '13px', color: '#6E7F5B', textAlign: 'center' }}>
              {language === 'tr' ? 'Rezervasyon bilgileriniz kayıt edildi. Ödemeyi tamamlamak için devam edin.' : 'Your reservation details have been saved. Continue to complete payment.'}
            </p>
          </div>
        </div>

        {/* Security badges */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          {[
            { icon: Lock, label: '256-bit SSL' },
            { icon: ShieldCheck, label: 'PCI-DSS' },
            { icon: CreditCard, label: language === 'tr' ? '3D Güvenli' : '3D Secure' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} style={{ background: 'white', borderRadius: '12px', padding: '12px', textAlign: 'center', border: '1px solid #EFE7DA' }}>
              <Icon size={20} style={{ color: '#1F3A2E', margin: '0 auto 6px', display: 'block' }} />
              <p style={{ fontSize: '11px', color: '#3E5A3F', fontWeight: 600 }}>{label}</p>
            </div>
          ))}
        </div>

        {/* Test mode warning */}
        {isTestMode && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '12px', marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <AlertTriangle size={16} style={{ color: '#d97706', flexShrink: 0, marginTop: '1px' }} />
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#92400e' }}>{t('test_mode')}</p>
              <p style={{ fontSize: '12px', color: '#92400e', marginTop: '2px' }}>
                {language === 'tr' ? 'Ödeme sağlayıcı bilgileri henüz eklenmemiş. Test modunda işlenecek.' : 'Payment provider credentials not configured. Processing in test mode.'}
              </p>
            </div>
          </div>
        )}

        {/* Pay Button */}
        <button
          onClick={handlePayment}
          disabled={processing}
          data-testid="payment-redirect-continue-button"
          style={{
            width: '100%', background: processing ? '#3E5A3F' : '#1F3A2E', color: 'white',
            padding: '16px', borderRadius: '14px', border: 'none', cursor: processing ? 'not-allowed' : 'pointer',
            fontSize: '16px', fontWeight: 700, fontFamily: 'Manrope, sans-serif',
            boxShadow: '0 4px 20px rgba(31,58,46,0.3)', transition: 'background 0.2s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
          }}
          onMouseEnter={e => !processing && (e.currentTarget.style.background = '#3E5A3F')}
          onMouseLeave={e => !processing && (e.currentTarget.style.background = '#1F3A2E')}
        >
          {processing ? (
            <>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
              <span data-testid="payment-redirect-status-text">{t('payment_processing')}</span>
            </>
          ) : (
            <>
              <Lock size={18} />
              {t('proceed_payment')}
            </>
          )}
        </button>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#6E7F5B', marginTop: '12px', lineHeight: 1.5 }}>
          {language === 'tr' ? 'TEB Sanal POS güvenli ödeme sayfasına yönlendirileceksiniz.' : 'You will be redirected to the TEB Virtual POS secure payment page.'}
        </p>

        {/* Accepted cards */}
        <div data-testid="payment-card-logos" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '16px' }}>
          {[
            { key: 'visa', el: <span style={{ fontStyle: 'italic', fontWeight: 800, fontSize: '14px', color: '#1A1F71' }}>VISA</span> },
            { key: 'mc', el: (
              <svg width="28" height="18" viewBox="0 0 30 19">
                <circle cx="10" cy="9.5" r="9" fill="#EB001B" />
                <circle cx="20" cy="9.5" r="9" fill="#F79E1B" fillOpacity="0.9" />
              </svg>
            ) },
            { key: 'troy', el: <img src="/logos/troy.png" alt="TROY" style={{ height: '13px', width: 'auto', display: 'block' }} /> },
          ].map(({ key, el }) => (
            <div key={key} style={{
              background: 'white', border: '1px solid #EFE7DA', borderRadius: '8px',
              height: '36px', padding: '0 14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {el}
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
